// src/lib/api-client.ts
// Centralized API client for authenticated requests to FastAPI backend

import { supabase } from "./supabase";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

/**
 * Error thrown for non-2xx responses. Preserves the HTTP status and the parsed
 * response body (FastAPI's `{ detail: ... }`) so callers can branch on structured
 * errors (e.g. a 409 `onestop_blocked` payload) instead of regex-ing the message.
 * `message` keeps the original human-readable format for backward compatibility.
 */
export class ApiError extends Error {
  status: number;
  /** Parsed JSON body when possible, otherwise the raw response text. */
  body: unknown;

  constructor(status: number, statusText: string, rawText: string) {
    let parsed: unknown = rawText;
    try {
      parsed = JSON.parse(rawText);
    } catch {
      // Non-JSON error body — keep the raw text.
    }
    super(`API request failed: ${status} ${statusText} - ${rawText}`);
    this.name = "ApiError";
    this.status = status;
    this.body = parsed;
  }
}

/**
 * Gets authentication headers for API calls
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();
  if (error) {
    console.error("Error getting session:", error);
    return { "Content-Type": "application/json" };
  }

  if (!session?.access_token) {
    console.warn(
      "No auth token found, proceeding without Authorization header."
    );
    return { "Content-Type": "application/json" };
  }

  return {
    Authorization: `Bearer ${session.access_token}`,
    "Content-Type": "application/json",
  };
}

/**
 * The signed-in user's access token, or null. For a caller that must hold the token before it can
 * no longer wait for it: a request sent while the page unloads (`sendKeepalive`).
 */
export async function readAccessToken(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  } catch {
    return null;
  }
}

/**
 * Send a request that outlives the page (review finding Q8-5): a `fetch` with `keepalive`, started
 * synchronously, for a `pagehide` handler. Nothing may be awaited before it starts (the page can
 * be gone by then), so the access token is passed in, read earlier with `readAccessToken`.
 * Fire-and-forget: the page that sent it is not there to read the answer. Returns false when the
 * browser refused to start it.
 */
export function sendKeepalive(endpoint: string, method: "DELETE" | "POST", accessToken: string | null): boolean {
  try {
    void fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      keepalive: true,
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    }).catch(() => undefined);
    return true;
  } catch {
    return false;
  }
}

/**
 * Extracts the filename from a Content-Disposition header, e.g.
 * `attachment; filename="terms.pdf"` or the RFC 5987 extended form
 * `attachment; filename*=UTF-8''terms.pdf`. Returns null when the header is absent or
 * unparseable so callers fall back to a generated name — never save a file named "undefined".
 */
function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;

  const extended = header.match(/filename\*=(?:UTF-8''|utf-8'')?([^;]+)/i);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim().replace(/^["']|["']$/g, ""));
    } catch {
      // Malformed percent-encoding — fall through to the plain form below.
    }
  }

  const plain = header.match(/filename="?([^";]+)"?/i);
  if (plain?.[1]) {
    return plain[1].trim();
  }

  return null;
}

/**
 * The path a blocked account is parked on. Duplicated from `routes.tsx` rather than imported:
 * this module is loaded by nearly every page, and importing the route table here would drag the
 * whole router into every chunk.
 */
const BLOCKED_ACCOUNT_PATH = "/pending-approval";

/**
 * Recognise the account-status refusal and act on it once, centrally.
 *
 * The guards already keep a blocked account off every product surface, but they resolve from a
 * profile cache with a 60s TTL — so an account SUSPENDED mid-session keeps making calls until the
 * next navigation reads a fresh profile. Each of those calls now 403s, and without this the user
 * would collect a string of failure toasts on a page that still looks like it works.
 *
 * Only the structured `reason` is matched, never a message string: the whole point of the backend
 * sending a code is that the client is not left parsing prose.
 */
function handleAccountBlocked(error: ApiError): void {
  if (error.status !== 403) return;
  const body = error.body as { detail?: { reason?: string } } | undefined;
  if (body?.detail?.reason !== "account_access_blocked") return;
  if (typeof window === "undefined") return;
  // Already there — never bounce the waiting page off itself.
  if (window.location.pathname === BLOCKED_ACCOUNT_PATH) return;
  window.location.assign(BLOCKED_ACCOUNT_PATH);
}

/**
 * Generic API request function with authentication
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const authHeaders = await getAuthHeaders();

  const headers =
    options.body instanceof FormData
      ? { Authorization: (authHeaders as Record<string, string>).Authorization }
      : authHeaders;

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    const error = new ApiError(response.status, response.statusText, errorText);
    handleAccountBlocked(error);
    throw error;
  }

  return await response.json();
}

/**
 * API client with common HTTP methods
 */
export const apiClient = {
  /**
   * GET request
   */
  async get<T>(endpoint: string, options?: { signal?: AbortSignal }): Promise<T> {
    // `signal` mirrors post()/put() — GET was the only verb without it. A typeahead needs it: a
    // slow early keystroke can otherwise resolve after a fast later one and overwrite the newer
    // results with stale ones.
    return apiRequest<T>(endpoint, { method: "GET", signal: options?.signal });
  },

  /**
   * POST request
   */
  async post<T>(
    endpoint: string,
    data?: any,
    options?: { signal?: AbortSignal }
  ): Promise<T> {
    return apiRequest<T>(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
      signal: options?.signal,
    });
  },

  /**
   * GET request that returns a binary Blob instead of parsed JSON. `apiRequest` (and therefore
   * `get()`) unconditionally calls `response.json()`, which throws on a non-JSON body — so file
   * downloads (PDF exports, etc.) must go through this method instead. Reuses `getAuthHeaders()`
   * and throws the same `ApiError` on a non-OK response as every other apiClient method, so
   * callers handle auth + errors uniformly. Do not hand-roll a bare `fetch()` for this — it would
   * ship without the Authorization header.
   */
  async getBlob(
    endpoint: string,
    options?: { signal?: AbortSignal }
  ): Promise<{ blob: Blob; filename: string | null }> {
    const authHeaders = await getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "GET",
      headers: authHeaders,
      signal: options?.signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      const error = new ApiError(response.status, response.statusText, errorText);
      handleAccountBlocked(error);
      throw error;
    }

    const blob = await response.blob();
    const filename = parseContentDispositionFilename(
      response.headers.get("Content-Disposition")
    );
    return { blob, filename };
  },

  /**
   * POST request with URL-encoded data
   */
  async postForm<T>(endpoint: string, data: string): Promise<T> {
    return apiRequest<T>(endpoint, {
      method: "POST",
      body: data,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: any): Promise<T> {
    return apiRequest<T>(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<T> {
    return apiRequest<T>(endpoint, { method: "DELETE" });
  },

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return apiRequest<T>(endpoint, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  /**
   * POST request with FormData (for file uploads)
   */
  async postFormData<T>(
    endpoint: string,
    formData: FormData,
    options?: { signal?: AbortSignal }
  ): Promise<T> {
    return apiRequest<T>(endpoint, {
      method: "POST",
      body: formData,
      signal: options?.signal,
    });
  },

  /**
   * PATCH request with FormData (partial update that may carry a file)
   */
  async patchFormData<T>(endpoint: string, formData: FormData): Promise<T> {
    return apiRequest<T>(endpoint, { method: "PATCH", body: formData });
  },

  /**
   * Upload file with authentication
   */
  async uploadFile<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, any>
  ): Promise<T> {
    const formData = new FormData();
    formData.append("file", file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    return this.postFormData<T>(endpoint, formData);
  },

  /**
   * Upload file with a timeout for long-running operations (e.g. CSV imports).
   * Uses AbortController to cancel the request if it exceeds the timeout.
   */
  async uploadFileWithTimeout<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, any>,
    timeoutMs: number = 600_000,
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const formData = new FormData();
    formData.append("file", file);
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    try {
      return await apiRequest<T>(endpoint, {
        method: "POST",
        body: formData,
        signal: controller.signal,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        const minutes = Math.round(timeoutMs / 60_000);
        throw new Error(
          `Request timed out after ${minutes} minute${minutes !== 1 ? "s" : ""}. The server may still be processing your upload.`
        );
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  },
};

export default apiClient;
